import { useCallback } from 'react'

import { Box, styled } from '@mui/material'
import { PickersDayProps } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { PickersDay } from '@mui/x-date-pickers/PickersDay'
import { format } from 'date-fns'

import type { ValidColor } from '@/local/components'
import { Dot } from '../../Dot'

import { useDateFnsLocale } from '../../../hooks/useDateFnsLocale'
import type {
  CalendarClickType,
  DateCalendarPublicProps as Props,
  SelectionAsRecord,
} from '../../typings'
import { parseDate } from '../../utils'

type CustomDayProps = PickersDayProps<Date> & {
  highlightedDays?: SelectionAsRecord
  dotColor: ValidColor
}

export function Calendar(props: Props) {
  const {
    selection,
    maxDate,
    minDate,
    disabledDays,
    highlightedDays,
    dotColor = '$pigmentGreen',
    onChange,
  } = props

  const minimumDate = minDate ? parseDate(minDate) : undefined
  const maximumDate = maxDate ? parseDate(maxDate) : undefined

  const locale = useDateFnsLocale()

  const handleOnChange = useCallback(
    (date: Date, type: CalendarClickType = 'click') => {
      onChange(date, selection, type)
    },
    [onChange, selection],
  )

  const shouldDisableDate = useCallback(
    (date: Date) => {
      const formattedDate = format(date, 'yyyyMMdd')
      return !!disabledDays && !!disabledDays[formattedDate]
    },
    [disabledDays],
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <CustomDateCalendar
        value={selection}
        onChange={value => handleOnChange(value)}
        minDate={minimumDate}
        maxDate={maximumDate}
        shouldDisableDate={shouldDisableDate}
        slots={{ day: CustomDay }}
        slotProps={{
          day: ({ day }) =>
            ({
              highlightedDays,
              dotColor,
              'data-testid': format(day, 'yyyyMMdd'),
            }) as any,
        }}
      />
    </LocalizationProvider>
  )
}

const CustomDateCalendar = styled(DateCalendar)(() => ({
  '&.MuiDateCalendar-root': {
    width: 'auto',
    height: 'auto',
    marginBottom: '32px',
  },
  '& .MuiPickersCalendarHeader-root': {
    padding: 0,
    marginTop: '24px',
  },
  '& .MuiPickersSlideTransition-root.MuiDayCalendar-slideTransition': {
    minHeight: '230px',
  },
}))

const CustomDay = (props: CustomDayProps) => {
  const { day, selected, highlightedDays, dotColor, ...other } = props

  const formattedDate = format(day, 'yyyyMMdd')
  const isHighlighted = !!highlightedDays && !!highlightedDays[formattedDate]

  return (
    <Box position="relative" display="flex">
      <PickersDay {...other} day={day} selected={selected} />
      {isHighlighted && <Dot background={dotColor} />}
    </Box>
  )
}
